import { ContentfulRichText } from '@pcln/contentful-components'
import { themeGet } from '@styled-system/theme-get'
import { Box, Container, Flex, Image, Layout, Text } from 'pcln-design-system'
import React from 'react'
import styled from 'styled-components'
import CouponClipboard from '../CouponClipboard'
import MerchImage from '../MerchImage'
import { isBackgroundDark } from '../utils'
import MerchModuleActions from './MerchModuleActions'

const ModuleWrapper = styled(Flex)`
  overflow: hidden;

  &&& ol {
    list-style-type: ${(props) => props.orderedListStyleType};
  }

  &&& ul {
    list-style-type: ${(props) => props.unorderedListStyleType};
  }

  &&& ol,
  &&& ul {
    margin-left: 0;
    padding-inline-start: 0;

    & li > div,
    li > p {
      margin: ${themeGet('space.2')} 0;
    }

    ${themeGet('mediaQueries.md')} {
      padding-inline-start: ${(props) => (props.hideListStyle ? 0 : 24)}px;
    }
  }
`

const OverlineImage = styled(Image)`
  max-height: 48px;
`

const ImageWrapper = styled(Flex)`
  display: ${(props) => (props.imageDisplayMobile ? 'flex' : 'none')};
  ${themeGet('mediaQueries.lg')} {
    display: flex;
  }
  ${themeGet('mediaQueries.md')} {
    display: flex;
  }
`

function MerchModule({
  actions,
  backgroundColor = 'background.light',
  body,
  bodyColor = 'text.base',
  boxProps = { p: [3, null, null, 4] },
  callToAction,
  columnVariation = ['100', null, '50-50'],
  coupon,
  disclaimer,
  headingMB = ['12px', null, null, 3],
  headingTextStyle = ['heading3', null, null, null, 'heading2'],
  headline,
  headlineColor = 'text.base',
  imageAlt,
  imageAspectRatio = '16/9',
  imageDisplayMobile = false,
  imageUrl,
  isBox = true,
  isMobile = false,
  mediaDisplay = 'contain',
  mediaPosition = 'right',
  minImageHeight = '250px',
  overlineImage,
  primaryCallToAction,
  secondaryCallToAction,
  subheading,
  subheadingTextStyle = ['subheading4', null, null, null, 'subheading3'],
  tagline,
  taglineColor = 'text.light',
  taglineTextStyle = 'overline',
  textAlign = 'left',
  orderedListStyleType = 'decimal',
  unorderedListStyleType = 'disc',
}) {
  const isDark = isBackgroundDark(backgroundColor)
  if (isDark) {
    headlineColor = 'text.lightest'
    taglineColor = 'text.lightest'
  }

  const wrapperDirection =
    mediaPosition === 'right' ? 'row-reverse' : mediaPosition === 'center' ? 'column-reverse' : 'row'
  const wrapperDirectionResponsive = [
    imageDisplayMobile ? 'column-reverse' : wrapperDirection,
    null,
    null,
    wrapperDirection,
  ]
  const textWrapperAlign = mediaPosition === 'center' && unorderedListStyleType === 'none' ? 'center' : 'left'

  const overlineImageSrc =
    overlineImage && (typeof overlineImage === 'string' ? overlineImage : overlineImage?.file?.url)

  const bodyProps = {
    color: isDark ? 'text.lightest' : bodyColor,
    mt: coupon ? 3 : undefined,
    textAlign,
    fontSize: 2,
    lineHeight: '28px',
    letterSpacing: '-0.006rem',
    ...body?.props,
  }

  const disclaimerTextProps = {
    color: isDark ? 'text.lightest' : 'text.light',
    pt: tagline || headline || body || actions ? 3 : undefined,
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.013rem',
    textAlign,
  }
  const disclaimerNode =
    disclaimer &&
    (disclaimer.data ? (
      <ContentfulRichText {...disclaimerTextProps} body={disclaimer} />
    ) : (
      <Text {...disclaimerTextProps}>{disclaimer}</Text>
    ))

  const MerchBody = () => {
    return (
      <Flex
        data-testid='merch-module-text-wrapper'
        flexDirection='column'
        height='100%'
        justifyContent='center'
        textAlign={textWrapperAlign}
      >
        {overlineImageSrc && (
          <Flex justifyContent={textAlign} mb={[0, null, null, 1]} pb={2} width='100%'>
            <OverlineImage src={overlineImageSrc} />
          </Flex>
        )}
        {tagline && (
          <Text color={taglineColor} mb={headline && 2} textAlign={textAlign} textStyle={taglineTextStyle}>
            {tagline}
          </Text>
        )}
        {headline && (
          <Text
            color={headlineColor}
            mb={subheading ? 1 : headingMB}
            mt={0}
            textAlign={textAlign}
            textStyle={headingTextStyle}
          >
            {headline}
          </Text>
        )}
        {subheading && (
          <Text color={headlineColor} mb={3} mt={0} textAlign={textAlign} textStyle={subheadingTextStyle}>
            {subheading}
          </Text>
        )}
        {coupon && (
          <Flex>
            <CouponClipboard {...coupon} mb={1} />
          </Flex>
        )}
        {body && (body?.props ? React.cloneElement(body, bodyProps) : <Text {...bodyProps}>{body}</Text>)}
        <MerchModuleActions
          actions={actions}
          hasContentAbove={Boolean(tagline || headline || body)}
          isDark={isDark}
          justifyContent={textAlign}
          primaryCallToAction={primaryCallToAction}
          secondaryCallToAction={secondaryCallToAction}
        />
        {disclaimer && disclaimerNode}
      </Flex>
    )
  }

  const MerchImageLogic = () => {
    return (
      <ImageWrapper
        alignItems='center'
        data-testid='image-wrapper'
        height='100%'
        imageDisplayMobile={imageDisplayMobile}
        justifyContent='center'
      >
        <MerchImage
          borderRadius='lg'
          callToAction={callToAction}
          imageAlt={imageAlt}
          imageAspectRatio={imageAspectRatio}
          imageUrl={imageUrl}
          minImageHeight={minImageHeight}
          objectFit={mediaDisplay}
        />
      </ImageWrapper>
    )
  }

  const MerchModuleLogic = () => {
    const hasImage = (isMobile && imageDisplayMobile && imageUrl) || (!isMobile && imageUrl)
    const layoutProps = {
      gap: ['md', null, null, 'lg'],
      rowGap: 'md',
      variation: columnVariation,
      width: '100%',
    }

    return (
      <ModuleWrapper
        data-testid='merch-module-wrapper'
        alignItems='center'
        flexDirection={wrapperDirectionResponsive}
        hideListStyle={textAlign === 'center' || textAlign === 'right'}
        imageDisplayMobile={imageDisplayMobile}
        justifyContent='center'
        maxWidth={1280}
        mx='auto'
        orderedListStyleType={orderedListStyleType}
        unorderedListStyleType={unorderedListStyleType}
        width='100%'
      >
        {/* Assuming it's not mobile, behave as expected based on mediaPosition */}
        {mediaPosition === 'right' && !isMobile ? (
          <Layout {...layoutProps} flexWrap={['wrap-reverse', null, null, null, 'wrap']}>
            <MerchBody />
            {hasImage && <MerchImageLogic />}
          </Layout>
        ) : mediaPosition === 'center' && !isMobile ? (
          <Layout {...layoutProps} variation='100'>
            {hasImage && <MerchImageLogic />}
            <MerchBody />
          </Layout>
        ) : mediaPosition === 'left' && !isMobile ? (
          <Layout {...layoutProps}>
            {hasImage && <MerchImageLogic />}
            <MerchBody />
          </Layout>
        ) : isMobile && !imageDisplayMobile ? (
          <Layout {...layoutProps}>
            <MerchBody />
          </Layout>
        ) : (
          <Layout {...layoutProps}>
            {hasImage && <MerchImageLogic />}
            <MerchBody />
          </Layout>
        )}
      </ModuleWrapper>
    )
  }

  return (
    <Container>
      {isBox ? (
        <Box
          data-testid='merch-module-box'
          borderRadius='xl'
          boxShadowSize='sm'
          color={backgroundColor}
          {...boxProps}
        >
          <MerchModuleLogic />
        </Box>
      ) : (
        <Container data-testid='merch-module-container' color={backgroundColor}>
          <MerchModuleLogic />
        </Container>
      )}
    </Container>
  )
}

export default MerchModule
